import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Search, RotateCcw, ExternalLink, Trash2, Plus, Calendar as CalendarIcon, MapPin, Clock } from 'lucide-react';
import { calendarService, CalendarListEntry, CalendarEvent } from '../services/calendarService';
import { useNavigate, useLocation } from 'react-router-dom';
import PageTutorialButton from '@/components/common/PageTutorialButton';

type ViewType = 'calendars';

const DISPLAY_LOCALE = 'es-CO';

type DisplayDate = { month: string; day: string; time: string };

/** Renders in the source calendar's zone so times match Google Calendar, not the viewer's machine. */
function formatInstant(iso: string, timeZone?: string | null): DisplayDate {
    const instant = new Date(iso);
    const zone = timeZone ?? undefined;
    return {
        month: instant.toLocaleDateString(DISPLAY_LOCALE, { month: 'short', timeZone: zone }),
        day: instant.toLocaleDateString(DISPLAY_LOCALE, { day: 'numeric', timeZone: zone }),
        time: instant.toLocaleTimeString(DISPLAY_LOCALE, { hour: '2-digit', minute: '2-digit', timeZone: zone }),
    };
}

function formatEventStart(event: CalendarEvent): DisplayDate | null {
    if (event.start.dateTime) return formatInstant(event.start.dateTime, event.calendar_time_zone);
    if (!event.start.date) return null;
    // All-day `date` has no zone: build it locally, since `new Date('YYYY-MM-DD')` parses as UTC and shifts the day.
    const [year, month, day] = event.start.date.split('-').map(Number);
    return {
        month: new Date(year, month - 1, day).toLocaleDateString(DISPLAY_LOCALE, { month: 'short' }),
        day: String(day),
        time: 'Todo el día',
    };
}

/** Green once no reminder is pending; gray while one is still waiting to go out. */
function getReminderStatusLine(event: CalendarEvent): { text: string; className: string } | null {
    const status = event.reminder_status;
    if (!status || status.total === 0) return null;
    const progress = `${status.sent} de ${status.total}`;
    if (status.sent >= status.total) {
        return { text: `Recordatorios enviados (${progress})`, className: 'text-green-600' };
    }
    const next = status.next_at ? formatInstant(status.next_at, event.calendar_time_zone) : null;
    const nextText = next ? `${next.day} ${next.month}, ${next.time}` : null;
    if (status.sent > 0) {
        return {
            text: nextText ? `Enviado ${progress} · próximo: ${nextText}` : `Enviado ${progress}`,
            className: 'text-gray-400',
        };
    }
    return {
        text: nextText ? `Recordatorio programado: ${nextText} (${progress})` : `Recordatorio programado (${progress})`,
        className: 'text-gray-400',
    };
}

const CalendarIntegration: React.FC = () => {
    const { user, token } = useAuth();
    const userId = user?.id;
    const navigate = useNavigate();
    const location = useLocation();

    // View switcher
    const [view, setView] = useState<ViewType>('calendars');

    // State
    const [calendars, setCalendars] = useState<CalendarListEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [activating, setActivating] = useState<string | null>(null); // Track which calendar is being toggled
    const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [eventsPage, setEventsPage] = useState<number>(0);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [authUrl, setAuthUrl] = useState<string | null>(null);

    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Controlled dialog state for "Administrar Calendario"
    const [isManageDialogOpen, setIsManageDialogOpen] = useState<boolean>(false);
    // Flag set synchronously on OAuth success; a watching effect opens the dialog once calendars load
    const [shouldOpenManageDialog, setShouldOpenManageDialog] = useState<boolean>(false);

    // Avoid double-handling OAuth URL/postMessage under StrictMode
    const oauthUrlHandledRef = useRef(false);
    const oauthMessageHandledRef = useRef(false);
    const eventsLoadedWhileAuthRef = useRef(false);

    const fetchAuthUrl = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/google-sheets/auth?context=calendar`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const data = response.data;
            if (data.authorization_url) {
                setAuthUrl(data.authorization_url);
            }
        } catch (e) {
            console.error("Error fetching auth url", e);
            toast.error("Error al preparar la autenticación");
        }
    }, [token]);

    const checkAuthAndLoadCalendars = useCallback(async (force = false) => {
        try {
            setLoading(true);
            const fetchedCalendars = await calendarService.listCalendars({ force });
            setCalendars(fetchedCalendars);
            setIsAuthenticated(true);
        } catch (error: any) {
            console.error("Error loading calendars:", error);
            const status = error.response?.status;
            if (status === 401 || status === 403 || status === 500) {
                setIsAuthenticated(false);
                eventsLoadedWhileAuthRef.current = false;
                setEvents([]);
                fetchAuthUrl();
            } else {
                toast.error("Error al cargar calendarios");
            }
        } finally {
            setLoading(false);
        }
    }, [fetchAuthUrl]);

    const loadAllEvents = useCallback(async (force = false) => {
        setLoadingEvents(true);
        setEventsPage(0);
        try {
            const fetchedEvents = await calendarService.getAllActiveCalendarEvents({ force });
            setEvents(fetchedEvents);
        } catch (error: any) {
            const status = error?.response?.status;
            if (status !== 401 && status !== 403) {
                console.error("Error loading events:", error);
            }
            setEvents([]);
        } finally {
            setLoadingEvents(false);
        }
    }, []);

    // Initial load once auth is ready (stable deps — avoid re-fetch on user object identity changes)
    useEffect(() => {
        if (!userId || !token) return;
        void checkAuthAndLoadCalendars();
    }, [userId, token, checkAuthAndLoadCalendars]);

    // Events once per authenticated session on this page (refresh/toggle still force)
    useEffect(() => {
        if (!isAuthenticated) {
            eventsLoadedWhileAuthRef.current = false;
            setEvents([]);
            return;
        }
        if (eventsLoadedWhileAuthRef.current) return;
        eventsLoadedWhileAuthRef.current = true;
        void loadAllEvents();
    }, [isAuthenticated, loadAllEvents]);

    // Handle OAuth callback — listener for postMessage (standard) or URL params (fallback)
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;

            // OAuth success
            if (
                event.data?.type === 'google_oauth_success' &&
                event.data?.context === 'calendar'
            ) {
                if (oauthMessageHandledRef.current) return;
                oauthMessageHandledRef.current = true;
                console.log('Google OAuth success message received in CalendarIntegration');
                toast.success('Conexión con Google Calendar exitosa');
                eventsLoadedWhileAuthRef.current = false;
                void checkAuthAndLoadCalendars(true);
                setShouldOpenManageDialog(true);
                return;
            }

            // OAuth cancelled by user
            if (
                event.data?.type === 'google_oauth_cancelled' &&
                event.data?.context === 'calendar'
            ) {
                console.log('Google OAuth cancellation received in CalendarIntegration');
                toast.info('Conexión con Google Calendar cancelada');
                return;
            }

            // OAuth error
            if (
                event.data?.type === 'google_oauth_error' &&
                event.data?.context === 'calendar'
            ) {
                console.error('Google OAuth error received in CalendarIntegration');
                toast.error('No fue posible conectar Google Calendar');
            }
        };

        window.addEventListener('message', handleMessage);

        // Fallback when OAuth redirects in the same window
        const params = new URLSearchParams(location.search);
        const authStatus = params.get('auth');

        if (authStatus === 'success') {
            if (!oauthUrlHandledRef.current) {
                oauthUrlHandledRef.current = true;
                toast.success('Conexión con Google Calendar exitosa');
                eventsLoadedWhileAuthRef.current = false;
                void checkAuthAndLoadCalendars(true);
                setShouldOpenManageDialog(true);
            }
            navigate(location.pathname, { replace: true });
        } else if (authStatus === 'cancelled') {
            toast.info('Conexión con Google Calendar cancelada');
            navigate(location.pathname, { replace: true });
        } else if (authStatus === 'error') {
            toast.error('No fue posible conectar Google Calendar');
            navigate(location.pathname, { replace: true });
        }

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [location.search, location.pathname, navigate, checkAuthAndLoadCalendars]);

    // Open the manage-calendar dialog once calendars have finished loading after OAuth
    useEffect(() => {
        if (shouldOpenManageDialog && !loading && calendars.length > 0) {
            setIsManageDialogOpen(true);
            setShouldOpenManageDialog(false);
        }
    }, [shouldOpenManageDialog, loading, calendars]);

    const toggleCalendarActivation = async (calendarId: string, currentState: boolean) => {
        try {
            setActivating(calendarId);
            const newState = !currentState;

            await axios.post(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/calendar/activate`, {
                calendar_id: calendarId,
                active: newState
            });

            setCalendars(prev => prev.map(cal =>
                cal.id === calendarId ? { ...cal, is_active: newState } : cal
            ));

            toast.success(newState ? "Calendario Conectado" : "Calendario Desconectado");

            // Refresh events to reflect the change
            void loadAllEvents(true);

        } catch (error: any) {
            console.error('Error toggling calendar:', error);
            const errorMessage = error.response?.data?.detail || "No se pudo actualizar el estado del calendario.";
            toast.error(errorMessage);
        } finally {
            setActivating(null);
        }
    };

    const handleConnect = async (forceFresh = false) => {
        let url = forceFresh ? null : authUrl;
        if (!url) {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/google-sheets/auth?context=calendar`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                url = response.data?.authorization_url;
            } catch (e) {
                toast.error("Error al preparar la autenticación");
                return;
            }
        }

        if (url) {
            oauthMessageHandledRef.current = false;
            const w = 600, h = 700;
            const left = window.screenX + (window.outerWidth - w) / 2;
            const top = window.screenY + (window.outerHeight - h) / 2;
            const popup = window.open(url, "google_calendar_auth", `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
            
            // Backup interval for closure
            const interval = setInterval(() => {
                if (!popup || popup.closed) {
                    clearInterval(interval);
                    setTimeout(() => {
                        eventsLoadedWhileAuthRef.current = false;
                        void checkAuthAndLoadCalendars(true);
                    }, 1000);
                }
            }, 1000);
        } else {
            toast.error("Recarga la página para intentar conectar nuevamente.");
        }
    };

    const resetAuth = async () => {
        try {
            await calendarService.resetCalendarAuth();
            toast.success('Google Calendar desconectado. Conecta la cuenta nueva.');
            setCalendars([]);
            setEvents([]);
            eventsLoadedWhileAuthRef.current = false;
            setIsAuthenticated(false);
            await handleConnect(true);
        } catch (error) {
            console.error('Error resetting auth:', error);
            toast.error('Error al desconectar la cuenta');
        }
    };

    // Helper for filtering/sorting
    const getFilteredCalendars = () => {
        let filtered = [...calendars];

        // Search
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(cal =>
                cal.summary.toLowerCase().includes(lowerTerm) ||
                (cal.description && cal.description.toLowerCase().includes(lowerTerm))
            );
        }

        // Sort
        filtered.sort((a, b) => {
            if (sortOrder === 'asc') {
                return a.summary.localeCompare(b.summary);
            } else {
                return b.summary.localeCompare(a.summary);
            }
        });

        return filtered;
    };

    const filteredCalendars = getFilteredCalendars();
    const activeCalendars = calendars.filter(c => c.is_active);

    const EVENTS_PER_PAGE = 10;
    const totalPages = Math.ceil(events.length / EVENTS_PER_PAGE);
    const pagedEvents = events.slice(eventsPage * EVENTS_PER_PAGE, (eventsPage + 1) * EVENTS_PER_PAGE);
    return (
        <div className="max-w-[1600px] mx-auto animate-fadeIn">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Google Calendar</h1>
                        <p className="text-sm sm:text-base text-gray-600 mt-1 leading-relaxed">
                            Gestiona tus calendarios y campañas de recordatorios
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-start gap-2 w-full sm:flex-nowrap sm:w-auto sm:justify-end">
                        <PageTutorialButton tutorialKey="google_calendar" />
                        {view === 'calendars' && calendars.length > 0 && (
                            <>
                                <Button
                                    onClick={() => void checkAuthAndLoadCalendars(true)}
                                    disabled={loading}
                                    variant="outline"
                                    className="bg-white hover:bg-slate-50 border-gray-200 text-gray-500 w-11 h-11 p-0 rounded-xl shadow-sm transition-all active:scale-95 group"
                                >
                                    <RotateCcw className={`w-5 h-5 transition-transform duration-500 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                                </Button>
                                <Button
                                    onClick={resetAuth}
                                    className="w-full sm:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-4 sm:px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Cambiar Cuenta
                                </Button>
                            </>
                        )}
                        {view === 'calendars' && calendars.length === 0 && (
                            <Button
                                onClick={() => handleConnect()}
                                className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-6 h-11 rounded-xl shadow-lg flex items-center gap-2 font-semibold transition-all active:scale-95"
                            >
                                <Plus className="w-5 h-5" />
                                Nueva Conexión
                            </Button>
                        )}
                    </div>
                </div>


                {/* Main Content Area — Calendars View */}
                {view === 'calendars' && (loading ? (
                    <div className="flex flex-col gap-6">
                        <Card className="w-full border-gray-200 shadow-sm animate-pulse">
                            <CardHeader className="bg-gradient-to-r from-[#f0f9fa] to-[#e6f7f4] border-b border-gray-100 h-16"></CardHeader>
                            <CardContent className="pt-6 pb-6">
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="w-48 h-6 bg-gray-200 rounded mb-2"></div>
                                            <div className="w-64 h-4 bg-gray-200 rounded"></div>
                                        </div>
                                        <div className="w-16 h-12 bg-gray-200 rounded"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 h-40 flex flex-col">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                                                    <div className="w-16 h-5 bg-gray-200 rounded-full"></div>
                                                </div>
                                                <div className="w-3/4 h-5 bg-gray-200 rounded mb-2"></div>
                                                <div className="w-full h-4 bg-gray-200 rounded mb-4"></div>
                                                <div className="flex gap-2 mt-auto">
                                                    <div className="w-1/2 h-8 bg-gray-200 rounded"></div>
                                                    <div className="w-8 h-8 bg-gray-200 rounded"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                ) : calendars.length === 0 ? ( 
                    <Card className="border-2 border-dashed border-gray-200 bg-gray-50/50">
                        <CardContent className="pt-12 pb-12">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-gradient-to-r from-[#e6f7f4] to-[#f0f9fa] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#e0f2f1]">
                                    <CalendarIcon className="h-8 w-8 text-[#1CB3B3]" />
                                </div>
                                <h3 className="text-xl font-medium text-gray-900 mb-2">
                                    Conecta Google Calendar
                                </h3>
                                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                    Conecta tu cuenta de Google para visualizar y gestionar tus eventos directamente desde aquí.
                                </p>
                                <div className="flex justify-center mt-4">
                                    <Button
                                        onClick={() => handleConnect()}
                                        className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-8 h-11 rounded-xl shadow-lg flex items-center gap-2 font-semibold transition-all active:scale-95"
                                    >
                                        <Plus className="w-5 h-5" />
                                        Conectar Google Calendar
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="flex flex-col gap-6">
                        {/* Status Card (Full Width) */}
                        <Card className="w-full border-gray-200 shadow-sm">
                            <CardHeader className="bg-gradient-to-r from-[#f0f9fa] to-[#e6f7f4] border-b border-gray-100 flex flex-col items-center text-center">
                                <CardTitle className="text-lg flex items-center justify-center gap-2 text-[#1F4068]">
                                    <CalendarIcon className="h-5 w-5 text-[#1CB3B3]" />
                                    Estado de Conexión
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 pb-6">
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold text-[#1F4068]">Calendarios Conectados</h3>
                                            <p className="text-sm text-gray-500">Conecta uno o más calendarios para sincronizar recordatorios.</p>
                                        </div>
                                        <div className="flex flex-col items-center">
                                            <span className="text-3xl font-bold text-[#1CB3B3]">{activeCalendars.length}</span>
                                            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Conectados</p>
                                        </div>
                                    </div>

                                    {activeCalendars.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {activeCalendars.map(cal => (
                                                <div
                                                    key={cal.id}
                                                    className="group relative bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:border-[#1CB3B3]/50"
                                                >
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className="p-2 bg-[#f0f9fa] rounded-lg group-hover:bg-[#e6f7f4] transition-colors">
                                                            <CalendarIcon className="w-5 h-5 text-[#1CB3B3]" />
                                                        </div>
                                                    </div>

                                                    <h4 className="font-semibold text-gray-900 truncate mb-1" title={cal.summary}>
                                                        {cal.summary}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 line-clamp-1 mb-4">
                                                        {cal.description || "Sin descripción"}
                                                    </p>

                                                    <div className="flex gap-2 mt-auto">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="flex-1 text-xs border-gray-200 text-gray-600 hover:text-[#1F4068] hover:bg-gray-50 h-8"
                                                            onClick={() => window.open(`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(cal.id)}`, '_blank')}
                                                        >
                                                            <ExternalLink className="w-3 h-3 mr-1.5" />
                                                            Abrir
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-8 text-center">
                                            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                                <CalendarIcon className="w-6 h-6 text-gray-400" />
                                            </div>
                                            <p className="text-gray-500 text-sm font-medium">No hay calendario conectado</p>
                                            <p className="text-gray-400 text-xs mt-1">Conecta uno para comenzar la sincronización</p>
                                        </div>
                                    )}

                                    <div className="pt-2 flex justify-center">
                                        <Dialog open={isManageDialogOpen} onOpenChange={setIsManageDialogOpen}>
                                            <DialogTrigger asChild>
                                                <Button
                                                    variant="default"
                                                    className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-6 h-11 rounded-xl shadow-lg flex items-center gap-2 font-semibold transition-all active:scale-95"
                                                >
                                                    <CalendarIcon className="w-5 h-5" />
                                                    Administrar Calendario
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">

                                                <DialogHeader>
                                                    <DialogTitle>Administrar Calendarios</DialogTitle>
                                                    <DialogDescription>
                                                        Activa los calendarios cuyos eventos generarán recordatorios automáticos por WhatsApp.
                                                    </DialogDescription>
                                                </DialogHeader>

                                                {/* Search in Modal */}
                                                <div className="py-2">
                                                    <div className="relative">
                                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                                        <input
                                                            type="text"
                                                            placeholder="Buscar..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-transparent"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                                                    {filteredCalendars.map((calendar) => (
                                                        <div key={calendar.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                                                            <div className="flex items-center gap-3 overflow-hidden">
                                                                <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                                                                    <CalendarIcon className="h-4 w-4 text-blue-500" />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-sm truncate max-w-[200px] sm:max-w-xs" title={calendar.summary}>
                                                                        {calendar.summary}
                                                                    </p>
                                                                    {calendar.primary && (
                                                                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Principal</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {activating === calendar.id ? (
                                                                <div className="w-10 h-5 flex items-center justify-center flex-shrink-0">
                                                                    <div className="w-4 h-4 border-2 border-[#1CB3B3] border-t-transparent rounded-full animate-spin" />
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className={`w-10 h-5 rounded-full p-1 cursor-pointer transition-colors duration-200 ease-in-out flex-shrink-0 ${calendar.is_active ? 'bg-[#1CB3B3]' : 'bg-gray-300'} ${activating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    onClick={() => !activating && toggleCalendarActivation(calendar.id, calendar.is_active || false)}
                                                                >
                                                                    <div
                                                                        className={`bg-white w-3 h-3 rounded-full shadow-md transform duration-200 ease-in-out ${calendar.is_active ? 'translate-x-5' : 'translate-x-0'}`}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {filteredCalendars.length === 0 && (
                                                        <div className="text-center py-8 text-gray-500">
                                                            No se encontraron calendarios.
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Footer with Create New Calendar */}
                                                <CreateCalendarForm onCreated={() => void checkAuthAndLoadCalendars(true)} />

                                            </DialogContent>
                                        </Dialog>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Events Viewer (Full Width Below) */}
                        <div className="w-full">
                            {activeCalendars.length > 0 ? (
                                <Card className="border-gray-200 shadow-sm h-full flex flex-col" id="calendar-events-viewer">
                                    <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-[#f0f9fa] to-[#e6f7f4] border-b border-gray-100">
                                        <div className="space-y-1">
                                            <CardTitle className="text-xl text-gray-800 flex items-center gap-2">
                                                <CalendarIcon className="h-5 w-5 text-[#1CB3B3]" />
                                                Próximos eventos
                                            </CardTitle>
                                            <CardDescription>
                                                {events.length > 0
                                                    ? `${events.length} evento${events.length !== 1 ? 's' : ''} próximo${events.length !== 1 ? 's' : ''} en todos los calendarios conectados`
                                                    : 'Eventos de todos los calendarios conectados'}
                                            </CardDescription>
                                        </div>
                                        <Button
                                            onClick={() => void loadAllEvents(true)}
                                            disabled={loadingEvents}
                                            variant="outline"
                                            className="bg-white hover:bg-slate-50 border-gray-200 text-gray-500 w-11 h-11 p-0 rounded-xl shadow-sm transition-all active:scale-95 group"
                                            title="Sincronizar eventos"
                                        >
                                            <RotateCcw className={`w-5 h-5 transition-transform duration-500 ${loadingEvents ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                                        </Button>
                                    </CardHeader>
                                    <CardContent className="flex-1 pt-6">
                                        {loadingEvents ? (
                                            <div className="space-y-3 animate-pulse">
                                                {[1, 2, 3].map(i => (
                                                    <div key={i} className="flex gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 items-center">
                                                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                                        <div className="flex-1 space-y-2 py-1">
                                                            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                                                            <div className="flex gap-2">
                                                                <div className="h-3 bg-gray-200 rounded w-16"></div>
                                                                <div className="h-3 bg-gray-200 rounded w-24"></div>
                                                            </div>
                                                        </div>
                                                        <div className="w-4 h-4 bg-gray-200 rounded-full flex-shrink-0"></div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : events.length > 0 ? (
                                            <>
                                                <div className="space-y-3">
                                                    {pagedEvents.map((event) => {
                                                        const reminderLine = getReminderStatusLine(event);
                                                        const calName = event.calendar_id
                                                            ? (calendars.find(c => c.id === event.calendar_id)?.summary || event.calendar_id)
                                                            : undefined;
                                                        const start = formatEventStart(event);
                                                        return (
                                                            <a
                                                                key={event.id}
                                                                href={event.htmlLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="flex gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 hover:shadow-md transition-all cursor-pointer group items-center"
                                                            >
                                                                <div className="flex-shrink-0 flex flex-col items-center justify-center w-12 h-12 bg-white rounded-lg border border-gray-200 shadow-sm group-hover:border-[#1CB3B3] transition-colors">
                                                                    <span className="text-[10px] font-semibold text-gray-500 uppercase leading-none mb-0.5">
                                                                        {start?.month ?? ''}
                                                                    </span>
                                                                    <span className="text-lg font-bold text-[#1CB3B3] leading-none">
                                                                        {start?.day ?? ''}
                                                                    </span>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <h4 className="font-semibold text-gray-900 truncate group-hover:text-[#1CB3B3] transition-colors text-sm mb-0.5">{event.summary || '(Sin título)'}</h4>
                                                                    <div className="flex flex-wrap items-center text-xs text-gray-500 gap-x-3 gap-y-1">
                                                                        <span className="flex items-center text-gray-600 font-medium">
                                                                            <Clock className="w-3 h-3 mr-1 text-[#1CB3B3]" />
                                                                            {start?.time ?? ''}
                                                                        </span>
                                                                        {event.location && (
                                                                            <span className="flex items-center truncate max-w-[150px]">
                                                                                <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                                                                                {event.location}
                                                                            </span>
                                                                        )}
                                                                        {event.description && (
                                                                            <span className="hidden sm:flex items-center border-l border-gray-300 pl-2 truncate max-w-[200px] text-gray-400">
                                                                                {event.description}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {reminderLine && (
                                                                        <p className={`mt-1 text-xs font-medium ${reminderLine.className}`}>
                                                                            {reminderLine.text}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <div className="flex-shrink-0 flex items-center gap-2">
                                                                    {calName && (
                                                                        <span className="text-[11px] text-gray-400 hidden sm:inline max-w-[100px] truncate">{calName}</span>
                                                                    )}
                                                                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#1CB3B3] transition-colors" />
                                                                </div>
                                                            </a>
                                                        );
                                                    })}
                                                </div>

                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                                                        <button
                                                            onClick={() => setEventsPage(p => Math.max(0, p - 1))}
                                                            disabled={eventsPage === 0}
                                                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            ← Anterior
                                                        </button>
                                                        <span className="text-xs text-gray-500">
                                                            Página {eventsPage + 1} de {totalPages} · {events.length} eventos
                                                        </span>
                                                        <button
                                                            onClick={() => setEventsPage(p => Math.min(totalPages - 1, p + 1))}
                                                            disabled={eventsPage >= totalPages - 1}
                                                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            Siguiente →
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-full text-center py-12 text-gray-500">
                                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                                    <CalendarIcon className="w-8 h-8 text-gray-300" />
                                                </div>
                                                <h3 className="text-lg font-medium text-gray-900">No hay eventos próximos</h3>
                                                <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">
                                                    No se encontraron eventos futuros en los calendarios conectados.
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-8 text-center text-gray-500">
                                    <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
                                        <Clock className="w-8 h-8 text-indigo-300" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900">Sin Calendario Conectado</h3>
                                    <p className="max-w-md mx-auto mt-2 text-gray-500">
                                        Conecta un calendario en el panel de arriba ("Administrar Calendarios") para comenzar a ver eventos aquí.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div >
    );
};

// ... existing helper component if any ...

// Separate component for Create Calendar Form to separate state
const CreateCalendarForm = ({ onCreated }: { onCreated: () => void }) => {
    const [summary, setSummary] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!summary.trim()) {
            toast.error("El nombre es obligatorio");
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/calendar`, {
                summary,
                description
            });
            toast.success("Calendario creado exitosamente");
            setSummary('');
            setDescription('');
            setIsOpen(false);
            onCreated(); // Refresh list
        } catch (error: any) {
            console.error("Error creating calendar", error);
            const msg = error.response?.data?.detail || "Error al crear calendario";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <div className="mt-4 pt-4 border-t border-gray-100">
                <Button
                    variant="ghost"
                    className="w-full justify-start text-[#1CB3B3] hover:text-[#189999] hover:bg-[#e6f7f7]"
                    onClick={() => setIsOpen(true)}
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Nuevo Calendario
                </Button>
            </div>
        );
    }

    return (
        <div className="mt-4 pt-4 border-t border-gray-100 bg-gray-50 rounded-lg p-4 animate-in fade-in slide-in-from-top-2">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center justify-between">
                <span>Nuevo Calendario</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setIsOpen(false)}>
                    <Trash2 className="h-3 w-3 text-gray-400 rotate-45" /> {/* Close icon */}
                </Button>
            </h4>
            <form onSubmit={handleCreate} className="space-y-3">
                <div>
                    <input
                        type="text"
                        placeholder="Nombre del calendario (ej. Sala 1)"
                        value={summary}
                        onChange={e => setSummary(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-transparent"
                        autoFocus
                    />
                </div>
                <div>
                    <input
                        type="text"
                        placeholder="Descripción (opcional)"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-transparent"
                    />
                </div>
                <div className="flex items-center gap-2 pt-1">
                    <Button
                        type="submit"
                        disabled={loading}
                        className="bg-[#1CB3B3] hover:bg-[#189999] text-white h-8 text-xs w-full"
                    >
                        {loading ? 'Creando...' : 'Crear Calendario'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default CalendarIntegration;
